/**
 * A evidência da visita (ADR-038, spec visitas-sem-copia, Req 5): "Com imagem" (termo digitalizado) ou "Planilha (sem
 * imagem)" (registrada pela planilha de instalações) — e, na linha da planilha cuja escola tem termo, "Referência —
 * há termo com imagem". Componentes únicos usados em todas as telas que listam ou contam visitas:
 *  - `EtiquetaEvidencia`: a etiqueta (texto além da cor — acessível);
 *  - `FiltroEvidencia`: Todas / Com imagem / Planilha (sem imagem) e, com "Planilha", a caixa "Incluir referências"
 *    (`semReferencias`: sem a caixa — onde as referências não contam: escolas, painel, qualidade, mapa).
 */

import { Evidencia, FonteVisita } from '../services/consulta-types';

export const ROTULO_EVIDENCIA: Readonly<Record<Evidencia, string>> = {
  imagem: 'Com imagem',
  planilha: 'Planilha (sem imagem)',
};

export const ROTULO_REFERENCIA = 'Referência — há termo com imagem';

/**
 * ADR-046: o subtítulo da visita sem cópia que não veio da planilha de instalações — "Planilha (sem imagem) — planilha
 * de controle" / "— Relatório Fotográfico". A da planilha de instalações (e a API anterior) segue sem subtítulo.
 */
export const SUBTITULO_FONTE: Readonly<Partial<Record<FonteVisita, string>>> = {
  controle: 'planilha de controle',
  relatorio_fotografico: 'Relatório Fotográfico',
};

export function rotuloEvidencia(evidencia: Evidencia, fonte?: FonteVisita | null): string {
  const subtitulo = evidencia === 'planilha' && fonte ? SUBTITULO_FONTE[fonte] : undefined;
  return subtitulo ? `${ROTULO_EVIDENCIA[evidencia]} — ${subtitulo}` : ROTULO_EVIDENCIA[evidencia];
}

export function EtiquetaEvidencia({
  evidencia,
  referencia = false,
  fonte,
}: {
  readonly evidencia: Evidencia | null | undefined;
  readonly referencia?: boolean;
  /** ADR-046: a fonte da visita (o subtítulo da evidência). */
  readonly fonte?: FonteVisita | null;
}): JSX.Element | null {
  if (!evidencia) {
    return null; // API anterior à evidência: nada a mostrar
  }
  return (
    <span className="etiquetas-evidencia">
      <span className={`pill pill-evidencia-${evidencia}`}>{rotuloEvidencia(evidencia, fonte)}</span>
      {referencia && <span className="pill pill-evidencia-referencia">{ROTULO_REFERENCIA}</span>}
    </span>
  );
}

/** O filtro aplicado: `evidencia` vazia = todas; `incluirReferencias` só vale com "Planilha". */
export interface ValorFiltroEvidencia {
  readonly evidencia?: Evidencia;
  readonly incluirReferencias?: boolean;
}

export function FiltroEvidencia({
  valor,
  onMudar,
  id = 'filtro-evidencia',
  semReferencias = false,
}: {
  readonly valor: ValorFiltroEvidencia;
  readonly onMudar: (novo: ValorFiltroEvidencia) => void;
  readonly id?: string;
  /** Sem a caixa "Incluir referências" (as referências não contam nesta tela). */
  readonly semReferencias?: boolean;
}): JSX.Element {
  return (
    <div className="field filtro-evidencia">
      <label htmlFor={id}>Evidência</label>
      <select
        id={id}
        value={valor.evidencia ?? ''}
        onChange={(e) => {
          const evidencia = (e.target.value || undefined) as Evidencia | undefined;
          // Referências só existem entre as visitas da planilha: trocar de tipo desliga a caixa.
          onMudar(evidencia === 'planilha' ? { evidencia, incluirReferencias: valor.incluirReferencias } : { evidencia });
        }}
      >
        <option value="">Todas</option>
        <option value="imagem">{ROTULO_EVIDENCIA.imagem}</option>
        <option value="planilha">{ROTULO_EVIDENCIA.planilha}</option>
      </select>
      {valor.evidencia === 'planilha' && !semReferencias && (
        <label>
          <input
            type="checkbox"
            checked={valor.incluirReferencias === true}
            onChange={(e) => onMudar({ evidencia: 'planilha', incluirReferencias: e.target.checked || undefined })}
          />{' '}
          Incluir referências
        </label>
      )}
    </div>
  );
}
